import React, { useEffect, useState, useRef } from "react";
import "./chatWidget.css";
import { useApi } from "../../shared/api";
import ReactMarkdown from "react-markdown";
import { selectGeo } from "../../redux/slices/pincode";
import { selectLoggedUser } from "../../redux/slices/auth";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { SitePath } from "../../shared/constants";
import { DEFAULT_BOT_MESSAGE, useChatSession } from "./utils/useChatSession";

export default function ChatWidget() {
    const showChatWidgetOnThesePages = [SitePath.Home, SitePath.SELL_NOW_PAGE];
    const api = useApi()
    const [open, setOpen] = useState(false);
    const geoLocation = useSelector(selectGeo);
    const { pincode } = geoLocation;
    const { userId } = useSelector(selectLoggedUser);
    const location = useLocation();
    const showChatWidget = showChatWidgetOnThesePages.includes(location.pathname as SitePath)

    const {
        messages,
        setMessages,
        suggestions,
        setSuggestions,
        selsmartToken,
        setSelsmartToken,
        xSessionToken,
        setXSessionToken,
        getInactivityStatus,
        updateActivity,
        resetSession
    } = useChatSession();

    const [loading, setLoading] = useState(false);
    const [text, setText] = useState<string>("");

    // ── OTP state ─────────────────────────────────────────────────────────────
    const OTP_TIMER_SECONDS = 30;
    const OTP_MAX_RESENDS = 2;
    const [otpActive, setOtpActive] = useState(false);
    const [otpTimer, setOtpTimer] = useState(OTP_TIMER_SECONDS);
    const [otpResendCount, setOtpResendCount] = useState(0);
    const [otpLocked, setOtpLocked] = useState(false);

    const isOtpResponse = (text: string) =>
        /\b(otp|one[- ]time[- ]password|enter[- ]otp|verification[- ]code|verify[- ]otp|enter.*code)\b/i.test(text);

    // Bot confirmed OTP was resent (e.g. "OTP Resent, Please enter the OTP")
    const isOtpResentResponse = (text: string) =>
        /\b(otp resent|otp.*sent again|resent.*otp|new otp.*sent|sent.*new otp)\b/i.test(text);

    const isOtpLockedResponse = (text: string) =>
        /\b(locked|too many.*attempt|incorrect.*otp.*attempt|verification.*session.*locked|start over)\b/i.test(text);

    // Bot says wrong OTP but can try again (keep timer running)
    const isOtpRetryResponse = (text: string) =>
        /\b(incorrect.*otp|wrong.*otp|otp.*incorrect|otp.*wrong|attempt.*remaining|please try again|try again.*otp|exit.*verification|cancel.*verification)\b/i.test(text);

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, open]);

    // Prevent background page from scrolling while chat is open
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    // OTP countdown timer
    useEffect(() => {
        if (!otpActive || otpTimer <= 0) return;
        const interval = setInterval(() => {
            setOtpTimer((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [otpActive, otpTimer]);

    // ── Auth-state change detection ────────────────────────────────────────────
    // When the user logs in or out mid-conversation, the previously stored
    // selsmartToken holds a stale JWT payload. Clearing it here forces the
    // backend to regenerate it with the correct isAuthenticated / userId.
    const prevUserIdRef = useRef<number | null | undefined>(undefined);
    useEffect(() => {
        // Skip the very first render (undefined → initial value)
        if (prevUserIdRef.current === undefined) {
            prevUserIdRef.current = userId;
            return;
        }
        if (prevUserIdRef.current !== userId) {
            prevUserIdRef.current = userId;
            setSelsmartToken("");
        }
    }, [userId]);



    const handleSend = async (e: React.FormEvent | [string, string, boolean]) => {
        let payload = null // { label: "", categoryId: "", pincode: "" };
        let messageText = text;

        if (Array.isArray(e)) {
            // Suggestion chip clicked
            payload = { label: e[0], categoryId: e[1], pincode: pincode as string };
            messageText = e[0];
        } else {
            // Form submit
            e.preventDefault();
        }

        if (loading || !messageText.trim()) return;

        let chatHistory = messages.filter((m) => m.role !== "error");
        let sToken = selsmartToken;
        let xToken = xSessionToken;

        // Reset if inactivity timeout is hit
        if (getInactivityStatus()) {
            chatHistory = [DEFAULT_BOT_MESSAGE];
            sToken = "";
            xToken = "";
            setMessages(chatHistory);
            setSuggestions([["", "", false]]);
            setSelsmartToken("");
            setXSessionToken("");
        }

        updateActivity();
        // Reset OTP locked flag on every new send, but do NOT touch otpActive/otpTimer here
        // — the timer must keep running while the user retries a wrong OTP.
        setOtpLocked(false);
        setMessages((prev) => [...prev, { text: messageText, role: "user", ts: new Date().toISOString() }]);
        setText("");
        setSuggestions([["", "", false]]);
        setMessages((prev) => [...prev, { text: "Loading...", role: "bot", ts: new Date().toISOString() }]);
        setLoading(true);

        try {
            const res = await api.API_V1.getChatWidget({
                message: messageText,
                chathistory: chatHistory,
                payload,
                selsmartToken: sToken,
                xSessionToken: xToken
            });
            if (res.data.suggestions && res.data.suggestions.length > 0) {
                setSuggestions(res.data.suggestions);
            } else {
                setSuggestions([["", "", false]]);
            }
            setSelsmartToken(res.data.selsmart_token);
            setXSessionToken(res.data.session_token);
            if (res.data.error || res.data.detail) {
                setMessages((prev) => [
                    ...prev.slice(0, -1),
                    { text: "Sorry, something went wrong. Please try again.", role: "error", ts: new Date().toISOString() },
                ]);
            } else {
                const replyText = res.data.reply;
                setMessages((prev) => [
                    ...prev.slice(0, -1),
                    { text: replyText, role: "bot", ts: new Date().toISOString() },
                ]);
                // Detect locked session — hide the entire OTP UI
                if (isOtpLockedResponse(replyText)) {
                    setOtpLocked(true);
                    setOtpActive(false);
                    // Bot confirmed OTP was re-sent — reset timer to full 30s
                } else if (isOtpResentResponse(replyText)) {
                    setOtpLocked(false);
                    setOtpActive(true);
                    setOtpTimer(OTP_TIMER_SECONDS);
                    // Wrong OTP but can retry — keep the existing timer running, don't reset it
                } else if (isOtpRetryResponse(replyText)) {
                    // otpActive is already true; timer continues from wherever it is
                    setOtpLocked(false);
                    // Fresh OTP request from bot for the first time
                } else if (isOtpResponse(replyText)) {
                    setOtpLocked(false);
                    setOtpActive(true);
                    setOtpTimer(OTP_TIMER_SECONDS);
                    // Reset resend count only for a fresh OTP flow, not after a resend request
                    if (messageText.toLowerCase() !== "resend otp") {
                        setOtpResendCount(0);
                    }
                } else {
                    // Non-OTP bot reply — clear OTP UI
                    setOtpActive(false);
                }
            }
        } catch {
            setMessages((prev) => [
                ...prev.slice(0, -1),
                { text: "Sorry, something went wrong. Please try again.", role: "error", ts: new Date().toISOString() },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (otpResendCount >= OTP_MAX_RESENDS || loading) return;
        setOtpResendCount((prev) => prev + 1);
        // Don't reset the timer here — wait for the bot's "OTP Resent" confirmation.
        // isOtpResentResponse() in handleSend will reset it once the reply arrives.
        await handleSend(["Resend OTP", "", false]);
    };

    return (
        <div className="chat-widget-wrapper">
            {open && (
                <div className="chat-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
                    {/* Close button visible on mobile above the sheet */}
                    <div className="chat-mobile-close">
                        <button onClick={() => setOpen(false)} aria-label="Close chat">✕</button>
                    </div>

                    <div className="chat-panel">
                        <div className="chat-header">Selsmart Buddy</div>
                        <div className="chat-body">
                            {messages.map((m, i) => (
                                <div key={i}>
                                    {m.role === "error" ? (
                                        <div className="msg-error-card">
                                            <div className="msg-error-header">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="15" y1="9" x2="9" y2="15" />
                                                    <line x1="9" y1="9" x2="15" y2="15" />
                                                </svg>
                                                <span>Error</span>
                                            </div>
                                            <p className="msg-error-body">{m.text}</p>
                                        </div>
                                    ) : (
                                        <>
                                            {m.role === "bot" ? <div className="bot-message-punch">Selsmart Buddy</div> : null}
                                            <div className={`msg ${m.role}`}>
                                                <ReactMarkdown>{m.text}</ReactMarkdown>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                            <div ref={messagesEndRef} />

                            {/* OTP Timer / Resend UI — hidden when session is locked */}
                            {otpActive && !otpLocked && (
                                <div className="otp-timer-container">
                                    {otpTimer > 0 ? (
                                        <p className="otp-timer-text">
                                            Resend OTP in <strong>{otpTimer}s</strong>
                                        </p>
                                    ) : otpResendCount < OTP_MAX_RESENDS ? (
                                        <button
                                            className="otp-resend-btn"
                                            onClick={handleResendOtp}
                                            disabled={loading}
                                        >
                                            🔁 Resend OTP {otpResendCount > 0 ? `(${OTP_MAX_RESENDS - otpResendCount} left)` : ""}
                                        </button>
                                    ) : (
                                        <p className="otp-resend-exhausted">Maximum resend attempts reached.</p>
                                    )}
                                </div>
                            )}
                            {suggestions.length > 1 && (
                                <div className="suggestions">
                                    {suggestions.map((s, i) => (
                                        <div key={i}>
                                            <button
                                                className="suggestion-btn"
                                                onClick={() => handleSend(s)}
                                                disabled={loading}
                                            >
                                                {s[0]}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>



                        <form className="chat-input" onSubmit={handleSend}>
                            <input
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={otpActive ? "Enter OTP…" : "Type a message…"}
                            />
                            <button type="submit" aria-label="Send message" disabled={loading || !text.trim()}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke={loading || !text.trim() ? "#ccc" : "#333"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                            </button>
                        </form>
                    </div>
                </div>
            )}
            {showChatWidget && (
                <div className="chat-bubble-wrapper">
                    {!open && (<div className="chat-bubble-text">Hello! How can I assist you today?</div>)}
                    <button className="chat-bubble" onClick={() => setOpen(!open)}>
                        {open ? <>⤬</> : <img src="/favicon.ico" alt="Chat" width={32} height={32} style={{ filter: "brightness(0) invert(1)" }} />}
                    </button>
                </div>
            )}
        </div>
    );
}


